import { useSelector } from 'react-redux';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { useLogger, useRouter, useServices } from '@packages/shared/core/common/hooks';
import { joinUrls } from '@packages/shared/core/common/utils/url';
import { SignUpEmailWithGenderDataHelpers, SignUpGoogleResponseData } from '@packages/shared/core/auth/entities';
import { getSignUpError, getUserPreferences } from '@packages/shared/core/user/store/selectors';
import { ApiError } from '@packages/shared/core/common/errors';
import { QuizScreenProps, SignUpWithGenderStepData } from '@packages/shared/core/quiz/entities';
import { useExperts } from '@packages/shared/core/experts/hooks';

type SignUpWithGenderStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => React.ReactNode;
};

const SignUpWithGenderStep = ({ id, renderQuizScreen }: SignUpWithGenderStepProps) => {
  const logger = useLogger();

  const router = useRouter();

  const { analyticsService, authService } = useServices();

  const {
    next,
    stepData,
    signUpWithGoogle,
    totalSteps,
    stepOrder,
    baseUrl,
    userPreferences,
    updateUserPreferences,
    flowName,
  } = useQuizStep(id);

  const { signUpByGoogleStatus } = useSelector(getUserPreferences);
  const errorMessage = useSelector(getSignUpError);
  const { selectedExpert } = useExperts({ flowName });

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const goal = (stepData as SignUpWithGenderStepData).getGoal(userPreferences.goal);

  const goBack = async () => {
    router.back();
  };

  const handleSubmit = async (
    data: { email: string; gender: string },
    { setErrors }: SignUpEmailWithGenderDataHelpers,
  ) => {
    try {
      analyticsService.registrationFormClicked('common');

      const isValidEmailPromise = authService.isValidEmail(data.email);
      const doesEmailExistsPromise = authService.doesEmailExists(data.email);

      const [doesEmailExist] = await Promise.all([doesEmailExistsPromise, isValidEmailPromise]);

      updateUserPreferences('email', data.email);
      updateUserPreferences('gender', data.gender);
      updateUserPreferences('emailAlreadyExists', doesEmailExist.exists);

      await next();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ email: err.originalError.message });
      } else {
        setErrors({ email: 'Something went wrong. Try again later' });
      }
    }
  };

  const doSignUpWithGoogle = async (data: SignUpGoogleResponseData) => {
    analyticsService.registrationFormClicked('google');
    await signUpWithGoogle(data);
    return router.push(joinUrls(baseUrl, 'phoneNumber'));
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        goNext: handleSubmit,
        errorMessage,
        signUpWithGoogle: doSignUpWithGoogle,
        signUpByGoogleStatus,
        totalSteps,
        goal,
        stepOrder,
        expert: selectedExpert,
        ...(stepData as SignUpWithGenderStepData),
      })}
    </>
  );
};

export default SignUpWithGenderStep;
